import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTabTitle, subscribeTabTitles } from '../lib/tab-titles';
import { useT, type Translate } from '../lib/i18n';

interface NavItemLite {
  to: string;
  label: string;
}
interface Tab {
  path: string;
  label: string;
  pinned: boolean;
  /** Last time this tab was the active one — used for least-recently-used eviction. */
  lastUsed: number;
}

const MAX_TABS = 8;
const PIN_KEY = 'ciimar-pinned-tabs';

function loadPinned(): { path: string; label: string }[] {
  try {
    const raw = localStorage.getItem(PIN_KEY);
    return raw ? (JSON.parse(raw) as { path: string; label: string }[]) : [];
  } catch {
    return [];
  }
}
function savePinned(tabs: Tab[]) {
  try {
    localStorage.setItem(
      PIN_KEY,
      JSON.stringify(tabs.filter((t) => t.pinned).map(({ path, label }) => ({ path, label }))),
    );
  } catch {
    /* ignore */
  }
}

/**
 * Best label for a path: a title the page registered (e.g. a collaborator's
 * name) wins; then the nav model; then per-section fallbacks for detail routes
 * (so a person tab never inherits the list's "Colaboradores"); then the path.
 */
function labelFor(path: string, items: NavItemLite[], tr: Translate): string {
  const registered = getTabTitle(path);
  if (registered) return registered;
  const exact = items.find((i) => i.to === path);
  if (exact) return exact.label;
  if (path.startsWith('/people/')) return tr('tabbar.collaborator');
  if (path.startsWith('/submissions/')) return tr('tabbar.record');
  const prefix = items
    .filter((i) => i.to !== '/' && path.startsWith(`${i.to}/`))
    .sort((a, b) => b.to.length - a.to.length)[0];
  if (prefix) return prefix.label;
  return path;
}

const PIN_ICON = 'M12 21s7-6.5 7-11a7 7 0 1 0-14 0c0 4.5 7 11 7 11z';

/**
 * Browser-style tabs for the panels the user has opened, to jump between them.
 * Non-pinned tabs are transient (evicted past MAX_TABS); hovering a tab reveals
 * a pin to affix it as a permanent tab (persisted per browser). An overflow
 * button opens a searchable switcher so many tabs stay practical.
 */
export function TabBar({ navItems }: { navItems: NavItemLite[] }) {
  const location = useLocation();
  const navigate = useNavigate();
  const tr = useT();
  const path = location.pathname;
  const [tabs, setTabs] = useState<Tab[]>(() =>
    loadPinned().map((t) => ({ ...t, pinned: true, lastUsed: 0 })),
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [filter, setFilter] = useState('');
  const menuRef = useRef<HTMLDivElement | null>(null);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  // Re-render when a page registers/updates a tab title (e.g. a person's name).
  const [, forceRender] = useState(0);
  useEffect(() => subscribeTabTitles(() => forceRender((n) => n + 1)), []);

  useEffect(() => {
    setTabs((prev) => {
      const now = Date.now();
      // Already open: just mark it most-recently-used.
      if (prev.some((t) => t.path === path)) {
        return prev.map((t) => (t.path === path ? { ...t, lastUsed: now } : t));
      }
      const next = [
        ...prev,
        { path, label: labelFor(path, navItems, tr), pinned: false, lastUsed: now },
      ];
      if (next.length > MAX_TABS) {
        // Evict the least-recently-used non-pinned tab (not the current one),
        // so the tabs you actually return to survive.
        let victim = -1;
        let oldest = Infinity;
        next.forEach((t, i) => {
          if (!t.pinned && t.path !== path && t.lastUsed < oldest) {
            oldest = t.lastUsed;
            victim = i;
          }
        });
        if (victim >= 0) next.splice(victim, 1);
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  // Close the switcher on outside click.
  useEffect(() => {
    if (!menuOpen) return;
    const onDown = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [menuOpen]);

  function go(target: string) {
    setMenuOpen(false);
    setFilter('');
    navigate(target);
  }
  function close(target: string) {
    setTabs((prev) => {
      const next = prev.filter((t) => t.path !== target);
      savePinned(next);
      if (target === path) navigate(next[next.length - 1]?.path ?? '/');
      return next;
    });
  }
  function togglePin(target: string) {
    setTabs((prev) => {
      const next = prev.map((t) => (t.path === target ? { ...t, pinned: !t.pinned } : t));
      savePinned(next);
      return next;
    });
  }

  // Keyboard on a focused tab: arrows move between tabs, Delete/Backspace closes.
  function onTabKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, target: string) {
    const labels = Array.from(
      tabsRef.current?.querySelectorAll<HTMLButtonElement>('.tab-label') ?? [],
    );
    const i = labels.indexOf(e.currentTarget);
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      labels[Math.min(i + 1, labels.length - 1)]?.focus();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      labels[Math.max(i - 1, 0)]?.focus();
    } else if (e.key === 'Home') {
      e.preventDefault();
      labels[0]?.focus();
    } else if (e.key === 'End') {
      e.preventDefault();
      labels[labels.length - 1]?.focus();
    } else if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      // Move focus to a neighbour before the tab unmounts.
      (labels[i + 1] ?? labels[i - 1])?.focus();
      close(target);
    }
  }

  if (tabs.length < 2) return null;

  // Labels are resolved live so a late-registered title (person name) shows up.
  const resolved = tabs.map((t) => ({ ...t, label: labelFor(t.path, navItems, tr) }));
  const q = filter.trim().toLowerCase();
  const inMenu = q ? resolved.filter((t) => t.label.toLowerCase().includes(q)) : resolved;

  return (
    <div className="tab-bar">
      <div className="tab-bar-tabs" ref={tabsRef}>
        {resolved.map((t) => (
          <div
            key={t.path}
            className={`browser-tab${t.path === path ? ' active' : ''}${t.pinned ? ' pinned' : ''}`}
            onAuxClick={(e) => {
              // Middle-click closes the tab, like a browser.
              if (e.button === 1) {
                e.preventDefault();
                close(t.path);
              }
            }}
          >
            <button
              type="button"
              className="tab-label"
              title={t.label}
              aria-current={t.path === path ? 'page' : undefined}
              onClick={() => navigate(t.path)}
              onKeyDown={(e) => onTabKeyDown(e, t.path)}
            >
              {t.label}
            </button>
            <button
              type="button"
              className="tab-pin"
              title={t.pinned ? 'Desafixar' : 'Afixar'}
              aria-label={t.pinned ? 'Desafixar' : 'Afixar'}
              onClick={() => togglePin(t.path)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill={t.pinned ? 'currentColor' : 'none'}
                stroke="currentColor"
                strokeWidth={1.8}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d={PIN_ICON} />
              </svg>
            </button>
            <button
              type="button"
              className="tab-close"
              title="Fechar"
              aria-label="Fechar"
              onClick={() => close(t.path)}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
              >
                <path d="M6 6l12 12M18 6L6 18" />
              </svg>
            </button>
          </div>
        ))}
      </div>

      <div className="tab-switcher" ref={menuRef}>
        <button
          type="button"
          className="tab-switch-btn"
          title="Todos os separadores"
          aria-label="Todos os separadores"
          onClick={() => setMenuOpen((v) => !v)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {menuOpen && (
          <div className="tab-menu">
            <input
              className="tab-menu-search"
              type="search"
              autoFocus
              placeholder="Filtrar separadores…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
            <div className="tab-menu-list">
              {inMenu.length === 0 ? (
                <div className="tab-menu-empty muted">Sem separadores</div>
              ) : (
                inMenu.map((t) => (
                  <button
                    key={t.path}
                    type="button"
                    className={`tab-menu-item${t.path === path ? ' active' : ''}`}
                    onClick={() => go(t.path)}
                  >
                    {t.pinned && (
                      <svg
                        className="tab-menu-pin"
                        width="11"
                        height="11"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                        stroke="currentColor"
                        strokeWidth={1.5}
                      >
                        <path d={PIN_ICON} />
                      </svg>
                    )}
                    <span>{t.label}</span>
                  </button>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
